import { ChangeDetectionStrategy, Component, computed, Inject, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { ErrorStateMatcher } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ScheduleDatesInterface } from '@dr-customer-offers-ui/lib-interfaces';
import { BulkOperationBehavior, BulkType, NgxIntervalDataGridRowModel, Week } from '@ngx-interval-data-grid';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { DatePickerEnum, DatePickerType, NgxDateRangePickerOutput, parseDateFormat } from 'ngx-date-range-picker';
import { NgxDropdownOption } from 'ngx-dropdown';
import { Subject, takeUntil } from 'rxjs';
import {
  BulkInput,
  InputTypeEnum,
  MinuteFrequencyOptionEnum,
  PeriodOption,
  PeriodOptionEnum,
  PeriodOptionValue,
  RegConfig,
  TabOption,
  TabOptionsEnum
} from '../../../shared/models';
import { DataViewModelService } from '../../../shared/services/data-vm.service';
import { InternalService } from '../../../shared/services/internal.service';
import { MixPanelService } from '../../../shared/services/mixpanel.service';
import { requiredPickerDate, validOfferRangeOptOut } from './bulk-selection.validators';

@Component({
  selector: 'dr-customer-offers-ui-bulk-selection',
  templateUrl: './bulk-selection.component.html',
  styleUrls: ['./bulk-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BulkSelectionComponent implements OnDestroy {
  protected offerValue: FormControl<number | null> = new FormControl<number | null>(null);
  protected optOut: FormControl = new FormControl<boolean>(false, { nonNullable: true });
  protected offerLock: FormControl = new FormControl<boolean>(false, { nonNullable: true });

  protected startDate: FormControl<NgxDateRangePickerOutput | null> = new FormControl<NgxDateRangePickerOutput | null>(null, [
    requiredPickerDate('Start')
  ]);
  protected endDate: FormControl<NgxDateRangePickerOutput | null> = new FormControl<NgxDateRangePickerOutput | null>(null, [
    requiredPickerDate('End')
  ]);
  protected startDaySelectedPeriod: FormControl<PeriodOptionValue | null> = new FormControl(null, [Validators.required]);
  protected endDaySelectedPeriod: FormControl<PeriodOptionValue | null> = new FormControl({ value: null, disabled: true }, [Validators.required]);
  protected startHour12: FormControl<string> = new FormControl<string>('12', { nonNullable: true });
  protected startMinute: FormControl<string> = new FormControl<string>({ value: '00', disabled: true }, { nonNullable: true });
  protected startPeriod: FormControl<'am' | 'pm'> = new FormControl('am', { nonNullable: true });
  protected endHour12: FormControl<string> = new FormControl<string>('12', { nonNullable: true });
  protected endMinute: FormControl<string> = new FormControl<string>({ value: '00', disabled: true }, { nonNullable: true });
  protected endPeriod: FormControl<'am' | 'pm'> = new FormControl('am', { nonNullable: true });
  protected bulkOperationBehavior = BulkOperationBehavior.INCLUSIVE;

  private defaultWeekStart = moment().startOf('week');
  private defaultWeekEnd = moment().endOf('week').endOf('day');
  private weekStart = this.defaultWeekStart.clone();
  private weekEnd = this.defaultWeekEnd.clone();

  protected minStartDate: Date | null = null;
  protected minEndDate: Date | null = null;
  protected maxStartDate: Date | null = null;
  protected maxEndDate: Date | null = null;
  protected minuteFrequency = 30;

  protected maxBulkValue!: number;
  protected minBulkValue!: number;
  protected allowZeroValue!: boolean;

  protected invalidFormName: string | undefined;
  protected selectedType: DatePickerType = DatePickerEnum.DatePicker;
  protected selectedTabOption!: TabOption;
  protected TabOptionsEnum = TabOptionsEnum;
  protected regConfig!: RegConfig | null;
  protected isExternalUser = false;
  protected showOfferLock = false;

  protected startDateFormControl: FormControl<NgxDateRangePickerOutput | null> = new FormControl(null);
  protected endDateFormControl: FormControl<NgxDateRangePickerOutput | null> = new FormControl(null);

  protected periodOptions: PeriodOption[] = [
    { value: PeriodOptionEnum.Sunday, label: this.translate.instant('COM.LABELS.SUNDAY'), hidden: false },
    { value: PeriodOptionEnum.Monday, label: this.translate.instant('COM.LABELS.MONDAY'), hidden: false },
    { value: PeriodOptionEnum.Tuesday, label: this.translate.instant('COM.LABELS.TUESDAY'), hidden: false },
    { value: PeriodOptionEnum.Wednesday, label: this.translate.instant('COM.LABELS.WEDNESDAY'), hidden: false },
    { value: PeriodOptionEnum.Thursday, label: this.translate.instant('COM.LABELS.THURSDAY'), hidden: false },
    { value: PeriodOptionEnum.Friday, label: this.translate.instant('COM.LABELS.FRIDAY'), hidden: false },
    { value: PeriodOptionEnum.Saturday, label: this.translate.instant('COM.LABELS.SATURDAY'), hidden: false }
  ];
  protected bulkEditOperationDescription: string;
  protected startTimeOptions: NgxDropdownOption[] = [];
  protected endTimeOptions: NgxDropdownOption[] = [];

  protected customRangeForm: FormGroup;
  protected offerForm: FormGroup;

  protected bulkOperationOutcome: string;
  private defaultBulkOperationOutcome: string;
  private readonly ERROR_TRANSLATION_PREFIX = 'COM.ERRORS.BULK_SELECTION.';

  protected customErrorStateMatcher: ErrorStateMatcher = {
    isErrorState: (control) => {
      return !!this.offerForm.errors?.['offerValue'] && !!control?.touched;
    }
  };

  protected get isApplyDisabled(): boolean {
    return (
      !!this.offerForm.errors ||
      (this.selectedTabOption.value === TabOptionsEnum.Schedule
        ? !!this.startDaySelectedPeriod.errors || !!this.endDaySelectedPeriod.errors
        : !!this.startDate.errors || !!this.endDate.errors) ||
      !this.isSelectedDateRangeValid()
    );
  }

  private isSelectedDateRangeValid(): boolean {
    const [startDate, endDate] = this.getStartAndEndDates();
    return moment.isMoment(startDate) && moment.isMoment(endDate) && startDate.isValid() && endDate.isValid() && endDate.isAfter(startDate);
  }

  protected get offerError() {
    const errors = this.offerForm.errors;
    if (!errors || !this.offerValue.touched) return null;
    return this.translate.instant(this.ERROR_TRANSLATION_PREFIX + errors['offerValue'], {
      minBulkValue: this.minBulkValue,
      maxBulkValue: this.maxBulkValue
    });
  }

  protected get startDateError() {
    const errors = this.startDate.errors;
    if (!errors || !this.startDate.touched) return null;
    return this.translate.instant(this.ERROR_TRANSLATION_PREFIX + errors['required']);
  }
  protected get endDateError() {
    const errors = this.endDate.errors;
    if (!errors || !this.endDate.touched) return null;
    return this.translate.instant(this.ERROR_TRANSLATION_PREFIX + errors['required']);
  }

  // This is just a cleaner approach to be more clear on start and end day. Can be removed if product dont like
  protected get startDayPeriodOptions(): PeriodOption[] {
    if (!this.endDaySelectedPeriod.value) {
      return this.periodOptions;
    }
    const endIndex = this.periodOptions.findIndex((option) => option.value === this.endDaySelectedPeriod.value);
    return this.periodOptions.slice(0, endIndex + 1);
  }

  protected get endDayPeriodOptions(): PeriodOption[] {
    if (!this.startDaySelectedPeriod.value) {
      return this.periodOptions;
    }
    const startIndex = this.periodOptions.findIndex((option) => option.value === this.startDaySelectedPeriod.value);
    return this.periodOptions.slice(startIndex);
  }

  protected get startPeriodError() {
    const errors = this.startDaySelectedPeriod.errors;
    if (!errors || !this.startDaySelectedPeriod.touched) return null;
    return this.translate.instant(this.ERROR_TRANSLATION_PREFIX + 'REQUIRED_START_DATE');
  }

  protected get endPeriodError() {
    const errors = this.endDaySelectedPeriod.errors;
    if (!errors || !this.endDaySelectedPeriod.touched) return null;
    return this.translate.instant(this.ERROR_TRANSLATION_PREFIX + 'REQUIRED_END_DATE');
  }

  private readonly destroy$ = new Subject();

  protected userLocale = computed(() => this.internalService.userPreferenceSignal()?.locale ?? 'en-US');
  protected datePickerHint = computed(() => moment().locale(this.userLocale()).localeData().longDateFormat('L') ?? 'MM/DD/YYYY');
  protected datePickerFormat = computed(() => parseDateFormat(this.datePickerHint()));

  constructor(
    private internalService: InternalService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      minuteFrequency: number;
      minBulkValue: number;
      maxBulkValue: number;
      minDate: moment.Moment;
      maxDate: moment.Moment;
      allowZeroValue: boolean | undefined;
      selectedTabOption: TabOption;
      regConfig: RegConfig | null;
      isExternalUser: boolean;
      showOfferLock: boolean;
    },
    public dialogRef: MatDialogRef<BulkSelectionComponent>,
    private translate: TranslateService,
    private mixPanelService: MixPanelService,
    private dataVMService: DataViewModelService
  ) {
    this.initData(data);
    this.bulkEditOperationDescription = this.getBulkOperationDescription(this.bulkOperationBehavior);

    this.customRangeForm = new FormGroup({
      bulkOperationBehavior: new FormControl<BulkOperationBehavior>(BulkOperationBehavior.INCLUSIVE),
      startDate: this.startDate,
      endDate: this.endDate,
      startDaySelectedPeriod: this.startDaySelectedPeriod,
      endDaySelectedPeriod: this.endDaySelectedPeriod,
      startHour12: this.startHour12,
      startMinute: this.startMinute,
      endHour12: this.endHour12,
      endMinute: this.endMinute,
      startPeriod: this.startPeriod,
      endPeriod: this.endPeriod
    });

    this.offerForm = new FormGroup(
      {
        optOut: this.optOut,
        offerValue: this.offerValue,
        ...(this.isExternalUser && this.showOfferLock ? {} : { offerLock: this.offerLock })
      },
      [validOfferRangeOptOut(this.minBulkValue, this.maxBulkValue, this.allowZeroValue)]
    );

    this.defaultBulkOperationOutcome = this.translate.instant('COM.DIALOGS.BULK.PENDING');
    this.bulkOperationOutcome = this.defaultBulkOperationOutcome;

    this.optOut.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((checked) => this.onOptOutValueChange(checked));

    this.startDate.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((d) => this.onStartEndDateValueChange(d, true));

    this.endDate.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((d) => this.onStartEndDateValueChange(d, false));

    this.startDaySelectedPeriod.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((selected) => this.onStartPeriodValueChange(selected));

    this.endDaySelectedPeriod.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.getBulkOutcomeMessage());

    if (!this.isExternalUser) this.offerLock.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.getBulkOutcomeMessage());

    this.customRangeForm
      .get('bulkOperationBehavior')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.getBulkOutcomeMessage());

    this.customRangeForm
      .get('startHour12')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.getBulkOutcomeMessage());

    this.customRangeForm
      .get('endHour12')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.getBulkOutcomeMessage());

    this.customRangeForm
      .get('startMinute')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.getBulkOutcomeMessage());

    this.customRangeForm
      .get('endMinute')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.getBulkOutcomeMessage());

    this.customRangeForm
      .get('startPeriod')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.getBulkOutcomeMessage());

    this.customRangeForm
      .get('endPeriod')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.getBulkOutcomeMessage());

    this.offerForm
      .get('offerValue')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.getBulkOutcomeMessage());

    this.internalService.selectedFilterDates.pipe(takeUntil(this.destroy$)).subscribe((selectedDate) => {
      this.weekStart = selectedDate?.start ?? this.defaultWeekStart.clone();
      this.weekEnd = selectedDate?.end ?? this.defaultWeekEnd.clone();
      this.minStartDate = this.weekStart.toDate();
      this.maxStartDate = this.weekEnd.toDate();
      this.minEndDate = this.weekStart.toDate();
      this.maxEndDate = this.weekEnd.toDate();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.unsubscribe();
  }

  getCurrentDates(): ScheduleDatesInterface {
    const n: NgxIntervalDataGridRowModel[] | null | undefined = this.internalService.scheduleDataSignal()?.values;
    if (!n || !n[0].Sunday.start_date_time) {
      // This part will be never entered as we will generate blank schedule if provided nothing.
      const today = moment().tz(this.internalService.scheduleDataSignal()?.regConfig?.timeZone ?? 'UTC');
      const startOfWeek = today.clone().startOf('week');
      return {
        Sunday: startOfWeek.clone(),
        Monday: startOfWeek.clone().add(1, 'day'),
        Tuesday: startOfWeek.clone().add(2, 'days'),
        Wednesday: startOfWeek.clone().add(3, 'days'),
        Thursday: startOfWeek.clone().add(4, 'days'),
        Friday: startOfWeek.clone().add(5, 'days'),
        Saturday: startOfWeek.clone().add(6, 'days')
      };
    }
    const timeZone = this.internalService.scheduleDataSignal()?.regConfig?.timeZone ?? 'UTC';

    const scheduleDates: ScheduleDatesInterface = {
      Sunday: moment(moment.utc(n[0].Sunday.start_date_time).tz(timeZone).format('YYYY-MM-DD HH:mm:ss')),
      Monday: moment(moment.utc(n[0].Monday.start_date_time).tz(timeZone).format('YYYY-MM-DD HH:mm:ss')),
      Tuesday: moment(moment.utc(n[0].Tuesday.start_date_time).tz(timeZone).format('YYYY-MM-DD HH:mm:ss')),
      Wednesday: moment(moment.utc(n[0].Wednesday.start_date_time).tz(timeZone).format('YYYY-MM-DD HH:mm:ss')),
      Thursday: moment(moment.utc(n[0].Thursday.start_date_time).tz(timeZone).format('YYYY-MM-DD HH:mm:ss')),
      Friday: moment(moment.utc(n[0].Friday.start_date_time).tz(timeZone).format('YYYY-MM-DD HH:mm:ss')),
      Saturday: moment(moment.utc(n[0].Saturday.start_date_time).tz(timeZone).format('YYYY-MM-DD HH:mm:ss'))
    };
    return scheduleDates;
  }

  protected apply(): void {
    this.mixPanelService.clickBulkApply();

    const [startDateAndTime, endDateAndTime] = this.getStartAndEndDates();

    const offerValue = this.offerValue.value;

    const behavior = this.customRangeForm.get('bulkOperationBehavior')!.value!;

    const output: BulkInput = {
      bulkType: BulkType.CUSTOM,
      inputType: offerValue !== null ? InputTypeEnum.VALUE : InputTypeEnum.OPTOUT,
      operation: behavior,
      value: offerValue ?? undefined,
      opt_out: this.optOut.value,
      offer_lock: this.isExternalUser && this.showOfferLock ? undefined : this.offerLock.value,
      week: undefined,
      startDateAndTime: startDateAndTime,
      endDateAndTime: endDateAndTime
    };
    this.internalService.setBulkInputs(output);
    this.dialogRef.close();
  }

  protected onBulkOperationBehaviorChange(change: MatButtonToggleChange) {
    const operation = change.value;
    this.bulkOperationBehavior = operation;
    this.bulkEditOperationDescription = this.getBulkOperationDescription(operation);
  }

  private createMomentWithDateAndTime(date: moment.Moment, hour12: string, minutes: string, period: string): moment.Moment | undefined {
    // Validate input values
    if (
      !date ||
      !date.isValid() || // Check if the date is valid
      +hour12 < 1 ||
      +hour12 > 12 || // Check if the hour is in the valid 1-12 range
      +minutes < 0 ||
      +minutes > 59 || // Check if minutes are in the valid 0-59 range
      (period !== 'am' && period !== 'pm') // Check if period is 'AM' or 'PM'
    ) {
      console.log('Invalid input values.');
      return undefined;
    }
    const timezone = this.internalService.scheduleDataSignal()?.regConfig?.timeZone ?? 'UTC';
    return moment.tz(date.format('YYYY-MM-DD ') + hour12 + ':' + minutes + period, 'YYYY-MM-DD h:mma', timezone);
  }

  private getBulkOperationDescription = (bulkOperationBehavior: BulkOperationBehavior): string => {
    const tableRefKey = this.selectedTabOption.value === TabOptionsEnum.Schedule ? 'SCHEDULE' : 'OPEN';
    return this.translate.instant(`COM.DIALOGS.BULK.${tableRefKey}.${bulkOperationBehavior.toUpperCase()}`);
  };

  private getBulkOutcomeMessage() {
    if (this.offerValue.value === null && !this.optOut.value) {
      this.bulkOperationOutcome = this.defaultBulkOperationOutcome;
      return;
    }
    const [startDate, endDate] = this.getStartAndEndDatesForSelectedTab();
    if (!startDate || !endDate) {
      this.bulkOperationOutcome = this.defaultBulkOperationOutcome;
      return;
    }
    // const offerValue = this.offerForm.get('offerValue')!.value
    const [startHour, endHour, startMinute, endMinute, startPeriod, endPeriod] = [
      this.customRangeForm.get('startHour12')?.value,
      this.customRangeForm.get('endHour12')?.value,
      String(this.customRangeForm.get('startMinute')?.value),
      String(this.customRangeForm.get('endMinute')?.value),
      this.customRangeForm.get('startPeriod')?.value,
      this.customRangeForm.get('endPeriod')?.value
    ];
    const behavior = this.customRangeForm.get('bulkOperationBehavior')!.value!.toUpperCase();
    if (!startHour && !endHour) {
      this.bulkOperationOutcome = this.defaultBulkOperationOutcome;
      return;
    }

    if (!this.isSelectedDateRangeValid()) {
      if (this.isSameDay(startDate, endDate)) {
        this.bulkOperationOutcome = this.translate.instant(this.ERROR_TRANSLATION_PREFIX + 'INVALID_START_END_TIMES');
        return;
      }
      this.bulkOperationOutcome = this.translate.instant(this.ERROR_TRANSLATION_PREFIX + 'INVALID_START_END_DATES');
      return;
    }

    const startHourMinute12 = this.getHourMinute12(String(startHour), startMinute, startPeriod);
    const endHourMinute12 = this.getHourMinute12(String(endHour), endMinute, endPeriod);

    if (this.selectedTabOption.value === TabOptionsEnum.Schedule) {
      const formattedStartDay = this.translate.instant('COM.DAYS.' + startDate.format('dddd').toUpperCase());
      const formattedEndDay = this.translate.instant('COM.DAYS.' + endDate.format('dddd').toUpperCase());
      if (this.isSameDay(startDate, endDate)) {
        this.bulkOperationOutcome = this.translate.instant('COM.DIALOGS.BULK.SCHEDULE.OUTCOME_SAME_DAY.' + behavior, {
          startTime: startHourMinute12,
          endTime: endHourMinute12,
          day: formattedStartDay
        });
      } else {
        this.bulkOperationOutcome = this.translate.instant('COM.DIALOGS.BULK.SCHEDULE.OUTCOME_DIFFERENT_DAYS.' + behavior, {
          startTime: startHourMinute12,
          endTime: endHourMinute12,
          startDay: formattedStartDay,
          endDay: formattedEndDay
        });
      }
    } else {
      const formattedStartDate = startDate.format('MM/DD/YYYY');
      const formattedEndDate = endDate.format('MM/DD/YYYY');
      if (this.isSameDay(startDate, endDate)) {
        this.bulkOperationOutcome = this.translate.instant('COM.DIALOGS.BULK.OPEN.OUTCOME_SAME_DAY.' + behavior, {
          startTime: startHourMinute12,
          endTime: endHourMinute12,
          date: formattedStartDate
        });
      } else {
        this.bulkOperationOutcome = this.translate.instant('COM.DIALOGS.BULK.OPEN.OUTCOME_DIFFERENT_DAYS.' + behavior, {
          startTime: startHourMinute12,
          endTime: endHourMinute12,
          startDate: formattedStartDate,
          endDate: formattedEndDate
        });
      }
    }
  }

  private isSameDay(date1: moment.Moment, date2: moment.Moment): boolean {
    return date1.isSame(date2, 'day');
  }

  private getStartAndEndDatesForSelectedTab(): [moment.Moment | undefined, moment.Moment | undefined] {
    if (this.selectedTabOption.value === TabOptionsEnum.Schedule) {
      return [this.getDateByWeekdayName(this.startDaySelectedPeriod.value), this.getDateByWeekdayName(this.endDaySelectedPeriod.value)];
    }
    return [this.startDate!.value?.start, this.endDate!.value?.start];
  }

  private getDateByWeekdayName(weekdayName: PeriodOptionValue | null) {
    if (!weekdayName) {
      return;
    }

    const targetDay = weekdayName.toLowerCase();

    for (let i = 0; i < 7; i++) {
      const currentDay = this.weekStart.clone().add(i, 'days');
      if (currentDay.format('dddd').toLowerCase() === targetDay) {
        return currentDay;
      }
    }
    return;
  }

  private getHourMinute12(hour: string, minute: string, period: string) {
    return hour + (minute !== '0' ? ':' + minute.padStart(2, '0') : '') + period;
  }

  private getStartAndEndDates() {
    let startDate, endDate;
    if (this.selectedTabOption.value === TabOptionsEnum.Schedule) {
      startDate = this.getCurrentDates()[this.startDaySelectedPeriod.value as Week];
      endDate = this.getCurrentDates()[this.endDaySelectedPeriod.value as Week];
    } else {
      startDate = this.startDate.value?.start;
      endDate = this.endDate.value?.start;
    }
    if (!startDate || !endDate) return [undefined, undefined];
    const startDateWithTime = this.createMomentWithDateAndTime(
      startDate,
      this.customRangeForm.get('startHour12')!.value!,
      this.customRangeForm.get('startMinute')!.value!,
      this.customRangeForm.get('startPeriod')!.value!
    );
    const endDateWithTime = this.createMomentWithDateAndTime(
      endDate,
      this.customRangeForm.get('endHour12')!.value!,
      this.customRangeForm.get('endMinute')!.value!,
      this.customRangeForm.get('endPeriod')!.value!
    );
    return [startDateWithTime, endDateWithTime];
  }

  private initData(data: {
    minuteFrequency: number;
    minBulkValue: number;
    maxBulkValue: number;
    minDate: moment.Moment;
    maxDate: moment.Moment;
    allowZeroValue: boolean | undefined;
    selectedTabOption: TabOption;
    regConfig: RegConfig | null;
    isExternalUser: boolean;
    showOfferLock: boolean;
  }) {
    this.selectedTabOption = data.selectedTabOption;
    this.regConfig = data.regConfig;
    this.isExternalUser = data.isExternalUser;
    this.showOfferLock = data.showOfferLock;

    // Check if the provided value is type MinuteFrecuencyOption
    if (this.isMinuteFrequencyOption(data.minuteFrequency)) {
      this.minuteFrequency = data.minuteFrequency as MinuteFrequencyOptionEnum;
    } else {
      // If not, set default and throw an warning
      console.warn('The minuteFrequency should be one of the following numbers: 0, 5, 10, 15, 30. The default of 30 mins as frequency will be used.');
      this.minuteFrequency = 30;
    }

    this.maxBulkValue = data.maxBulkValue;
    this.minBulkValue = data.minBulkValue;
    this.allowZeroValue = data.allowZeroValue ?? false;

    this.setMaxMinDates();
  }

  private isMinuteFrequencyOption(myVar: number): myVar is MinuteFrequencyOptionEnum {
    return myVar === 0 || myVar === 5 || myVar === 10 || myVar === 15 || myVar === 30;
  }

  private onOptOutValueChange(checked: boolean) {
    if (checked) {
      this.offerValue.setValue(null);
      this.offerValue.disable();
    } else {
      this.offerValue.enable();
    }
    this.getBulkOutcomeMessage();
  }

  private onStartEndDateValueChange(d: NgxDateRangePickerOutput | null, isStartDate = true) {
    if (isStartDate) {
      if (d?.start) {
        this.minEndDate = d.start.toDate();
      } else {
        this.minEndDate = this.weekStart.toDate();
      }
    } else {
      if (d?.start) {
        this.maxStartDate = d.start.toDate();
      } else {
        this.maxStartDate = this.weekEnd.toDate();
      }
    }
    this.getBulkOutcomeMessage();
  }

  private onStartPeriodValueChange(selected: PeriodOptionValue | null) {
    if (!selected) {
      this.endDaySelectedPeriod.disable();
    } else {
      this.endDaySelectedPeriod.enable();
    }
    this.endDaySelectedPeriod.setValue(null, { emitEvent: false });
    this.getBulkOutcomeMessage();
  }

  private setMaxMinDates(): void {
    const minDate = this.weekStart;
    this.minStartDate = minDate.toDate();
    this.minEndDate = minDate.toDate();
    this.maxStartDate = this.weekEnd.toDate();
    this.maxEndDate = new Date(this.maxStartDate);
  }
}
